import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { getRoleStyle, formatRole, countAll, flattenTree } from '@/components/OrgTreeNode';

function NodeRow(props) {
  var node = props.node;
  var depth = props.depth;
  var st = getRoleStyle(node.role);
  var total = countAll(node);
  var hasKids = node.children && node.children.length > 0;
  var isOpen = props.expanded.has(node.id);
  var isOver = props.overId === node.id;

  var cls = 'group flex items-center gap-3 p-3 rounded-lg border transition-all';
  if (node.role !== 'admin') cls += ' cursor-grab';
  if (props.draggedId === node.id) cls += ' opacity-40';
  if (isOver) cls += ' ring-2 ring-blue-500 border-blue-400 bg-blue-50 dark:bg-blue-900/20';
  else cls += ' bg-white dark:bg-slate-800 hover:shadow-md ' + st.border;

  return (
    <div style={{marginLeft: depth * 24 + 'px'}} className="my-1">
      <div
        draggable={node.role !== 'admin'}
        onDragStart={function(e) { e.stopPropagation(); props.onDragStart(node.id); }}
        onDragOver={function(e) { e.preventDefault(); e.stopPropagation(); props.setOverId(node.id); }}
        onDragLeave={function() { props.setOverId(null); }}
        onDrop={function(e) { e.preventDefault(); e.stopPropagation(); props.setOverId(null); if (props.draggedId && props.draggedId !== node.id) props.onDrop(props.draggedId, node.id); }}
        className={cls}
        data-testid={'org-node-' + node.id}
      >
        <button
          onClick={function() { props.toggleExpand(node.id); }}
          className={hasKids ? 'w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-600' : 'w-5 h-5 invisible'}
        >
          <svg className={isOpen ? 'w-3.5 h-3.5 rotate-90 transition-transform' : 'w-3.5 h-3.5 transition-transform'} fill="currentColor" viewBox="0 0 20 20"><path d="M6 6L14 10L6 14V6Z"/></svg>
        </button>

        <div className={'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ' + st.badge}>
          {(node.name || '?')[0].toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{node.name}</p>
            {node.designation && <span className="text-[10px] text-slate-400 hidden sm:inline">{node.designation}</span>}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={'text-[10px] font-medium px-1.5 py-0.5 rounded ' + st.badge}>{formatRole(node.role)}</span>
            {node.territories && node.territories.length > 0 && <span className="text-[10px] text-slate-400">{node.territories.join(', ')}</span>}
            {total > 0 && <span className="text-[10px] text-slate-400">{total} report{total !== 1 ? 's' : ''}</span>}
          </div>
        </div>

        <div className="hidden group-hover:flex items-center gap-1">
          <button onClick={function(e) { e.stopPropagation(); props.onEdit(node); }} className="p-1.5 text-slate-400 hover:text-blue-600 rounded" title="Edit">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
          </button>
          {node.role !== 'admin' && (
            <button onClick={function(e) { e.stopPropagation(); props.onRemove(node.id); }} className="p-1.5 text-slate-400 hover:text-red-600 rounded" title="Remove">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OrgChart() {
  var treeState = useState({ tree: [], unassigned: [] });
  var treeData = treeState[0]; var setTreeData = treeState[1];
  var loadState = useState(true); var loading = loadState[0]; var setLoading = loadState[1];
  var dragState = useState(null); var draggedId = dragState[0]; var setDraggedId = dragState[1];
  var expState = useState(new Set()); var expanded = expState[0]; var setExpanded = expState[1];
  var msgState = useState(null); var msg = msgState[0]; var setMsg = msgState[1];
  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var editState = useState(null); var editNode = editState[0]; var setEditNode = editState[1];
  var terrState = useState(''); var terrInput = terrState[0]; var setTerrInput = terrState[1];
  var desigState = useState(''); var desigInput = desigState[0]; var setDesigInput = desigState[1];
  var saveState = useState(false); var saving = saveState[0]; var setSaving = saveState[1];
  var overState = useState(null); var overId = overState[0]; var setOverId = overState[1];

  var fetchTree = useCallback(function() {
    return axios.get(API + '/users/org-tree').then(function(res) {
      setTreeData(res.data);
      var s = new Set();
      res.data.tree.forEach(function(r) { s.add(r.id); (r.children||[]).forEach(function(c) { s.add(c.id); }); });
      setExpanded(function(p) { return new Set([...p, ...s]); });
    }).catch(function() {}).finally(function() { setLoading(false); });
  }, []);

  useEffect(function() { fetchTree(); }, [fetchTree]);

  function flash(t, m) { setMsg({type:t,text:m}); setTimeout(function(){setMsg(null);},3000); }
  function toggle(id) { setExpanded(function(p) { var n=new Set(p); if(n.has(id)) n.delete(id); else n.add(id); return n; }); }

  function expandAll() {
    var a = new Set();
    function w(ns) { ns.forEach(function(n) { a.add(n.id); w(n.children||[]); }); }
    w(treeData.tree); setExpanded(a);
  }

  function handleDrop(dId, tId) {
    setDraggedId(null);
    axios.put(API+'/users/'+dId+'/manager',{manager_id:tId})
      .then(function(){flash('success','Hierarchy updated');return fetchTree();})
      .catch(function(e){flash('error',(e.response&&e.response.data&&e.response.data.detail)||'Failed');});
  }

  function handleRemove(uid) {
    axios.put(API+'/users/'+uid+'/manager',{manager_id:null})
      .then(function(){flash('success','Removed');return fetchTree();})
      .catch(function(e){flash('error',(e.response&&e.response.data&&e.response.data.detail)||'Failed');});
  }

  function handleSave() {
    if(!editNode) return; setSaving(true);
    var t = terrInput.split(',').map(function(x){return x.trim();}).filter(Boolean);
    axios.put(API+'/users/'+editNode.id+'/territories',{territories:t,designation:desigInput||null})
      .then(function(){flash('success','Updated');setEditNode(null);return fetchTree();})
      .catch(function(e){flash('error',(e.response&&e.response.data&&e.response.data.detail)||'Failed');})
      .finally(function(){setSaving(false);});
  }

  var flat = flattenTree(treeData.tree, expanded);

  var filtered = treeData.unassigned.filter(function(u) {
    if(!search) return true; var q=search.toLowerCase();
    return (u.name||'').toLowerCase().includes(q)||(u.email||'').toLowerCase().includes(q)||(u.role||'').includes(q);
  });

  if(loading) return <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4" data-testid="org-chart">
      {msg && <div className={msg.type==='success' ? 'px-4 py-2.5 rounded-lg text-sm font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' : 'px-4 py-2.5 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'}>{msg.text}</div>}

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 dark:text-slate-400">Drag users to set reporting. Drop onto a manager to assign.</p>
        <div className="flex gap-2">
          <button onClick={expandAll} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700" data-testid="expand-all-btn">Expand All</button>
          <button onClick={function(){setExpanded(new Set());}} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700" data-testid="collapse-all-btn">Collapse</button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 min-h-[200px]" data-testid="org-tree-container">
        {flat.length === 0
          ? <p className="text-center py-8 text-sm text-slate-400">No hierarchy configured. Drag from pool below.</p>
          : flat.map(function(item) {
            return <NodeRow key={item.node.id} node={item.node} depth={item.depth} onDragStart={setDraggedId} onDrop={handleDrop} onRemove={handleRemove} draggedId={draggedId} expanded={expanded} toggleExpand={toggle} overId={overId} setOverId={setOverId} onEdit={function(n){setEditNode(n);setTerrInput((n.territories||[]).join(', '));setDesigInput(n.designation||'');}} />;
          })
        }
      </div>

      <div className="bg-slate-50 dark:bg-slate-900 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-4" onDragOver={function(e){e.preventDefault();}} onDrop={function(e){e.preventDefault();if(draggedId) handleRemove(draggedId);}} data-testid="unassigned-pool">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Unassigned Users</h3>
            <span className="text-xs text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded-full">{treeData.unassigned.length}</span>
          </div>
          <input type="text" placeholder="Search..." value={search} onChange={function(e){setSearch(e.target.value);}} className="w-48 pl-3 pr-3 py-1.5 text-xs border rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="unassigned-search" />
        </div>
        {filtered.length === 0
          ? <p className="text-xs text-slate-400 text-center py-4">{search ? 'No match' : 'All assigned'}</p>
          : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
              {filtered.map(function(u) {
                var s = getRoleStyle(u.role);
                return (
                  <div key={u.id} draggable onDragStart={function(){setDraggedId(u.id);}} className={'flex items-center gap-2 p-2.5 rounded-lg border cursor-grab hover:shadow-sm bg-white dark:bg-slate-800 '+s.border} data-testid={'unassigned-'+u.id}>
                    <div className={'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold '+s.badge}>{(u.name||'?')[0].toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{u.name}</p>
                      <p className="text-[10px] text-slate-400 truncate">{formatRole(u.role)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>

      {editNode && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-5" data-testid="edit-territories-modal">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Edit Details</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{editNode.name} - {formatRole(editNode.role)}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Designation</label>
                <input type="text" value={desigInput} onChange={function(e){setDesigInput(e.target.value);}} placeholder="VP Sales APAC" className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="designation-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Territories</label>
                <input type="text" value={terrInput} onChange={function(e){setTerrInput(e.target.value);}} placeholder="India, UAE, Singapore" className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="territories-input" />
                <p className="text-xs text-slate-400 mt-1">Comma-separated</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={function(){setEditNode(null);}} className="px-4 py-2 text-sm border rounded-lg text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 disabled:opacity-50" data-testid="save-territories-btn">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
